import { Platform, Share } from 'react-native';
import i18n from '../i18n';
import { buildShareUrl } from './share';
import {
  trackShareClicked,
  trackShareCompleted,
  type ShareChannel,
  type ShareOutcome,
  type ShareSurface,
  type ShareTarget,
} from '../services/shareAnalytics';

/**
 * CORE-743 — one share text for a Live session, everywhere.
 *
 * Before this module every surface that offered «Поделиться» before the
 * broadcast rolled its own copy, and the three of them disagreed:
 *
 *   • calendar card  → `Join "{title}" with {master} on Agapē!` — English, and
 *                      with no URL at all, so the link was unshareable;
 *   • pre-broadcast  → the URL twice: the localized message already embeds it,
 *     («предбанник»)   and the same URL was handed to the share sheet a second
 *                      time as a separate field, which every target that
 *                      concatenates its inputs (system «Скопировать») dutifully
 *                      pasted twice;
 *   • Telegram       → correct, because it reads only the text field.
 *
 * The reference is the Telegram one, i.e. the localized `room.share.message`:
 *
 *     Присоединяйтесь к комнате «{title}» на Agapella
 *     {url}
 *
 * Two rules follow, and both live here rather than at the call sites:
 *
 *  1. The message is built from i18n (`room.share.*` / `lives:share.*` /
 *     `events:share.*`), never hand-written — that keeps ru/en/es in step and
 *     keeps English out of a Russian UI.
 *  2. **Never pass a separate `url` alongside a message that already contains
 *     it.** That is the whole of the duplication bug. Where a share channel
 *     takes one string (the OS sheet, the clipboard) it gets the message and
 *     nothing else.
 */

export type ShareEntityType =
  | 'room'      // watch-party / Live session — `/room/:id`
  | 'live'      // legacy live broadcast — `/live/:id`
  | 'event'     // offline event — `/lives?eventId=:id`
  | 'practice'; // recorded practice — `/practices/:id`

export interface ShareEntity {
  type: ShareEntityType;
  id: string | number;
  title?: string | null;
}

export interface ShareText {
  /** Headline without the URL — for channels that take text + link separately. */
  subject: string;
  /** Headline + newline + URL. The single string every one-field channel gets. */
  message: string;
  url: string;
}

/**
 * What actually happened, so a caller can show its «Ссылка скопирована» toast.
 * Defined once in `shareAnalytics` — the report and the toast must agree on
 * what «copied» means, and two unions that drift would be silent about it.
 */
export type { ShareOutcome };

const I18N_KEYS: Record<ShareEntityType, { subject: string; message: string; untitled: string }> = {
  room:  { subject: 'common:room.share.subject', message: 'common:room.share.message', untitled: 'common:room.share.untitled' },
  live:  { subject: 'lives:share.subject',       message: 'lives:share.message',       untitled: 'common:room.share.untitled' },
  event: { subject: 'events:share.subject',      message: 'events:share.message',      untitled: 'common:room.share.untitled' },
  practice: { subject: 'practices:share.subject', message: 'practices:share.message',  untitled: 'common:room.share.untitled' },
};

export function buildShareText(entity: ShareEntity): ShareText {
  const keys = I18N_KEYS[entity.type];
  // An untitled room used to fall back to the English «Live Room»; the label is
  // localized now so a Russian UI never leaks an English word into the message.
  const title = (entity.title ?? '').trim() || i18n.t(keys.untitled);
  const url = buildShareUrl(entity.type, entity.id);
  return {
    subject: i18n.t(keys.subject, { title }),
    // Interpolation runs with `escapeValue: false` (src/i18n/index.ts), so a
    // title carrying quotes or « » reaches the message intact.
    message: i18n.t(keys.message, { title, url }),
    url,
  };
}

/**
 * Hand the share text to whatever channel the platform offers.
 *
 * Web: the system share sheet when the browser has one, otherwise the
 * clipboard. Native: the OS share sheet.
 *
 * Nothing is awaited before `navigator.share()` — the Web Share API only
 * resolves inside a user gesture, and an await here would drop that gesture.
 *
 * CORE-788 — this is also the single instrumentation point for the OS-sheet
 * path: eleven call sites share through here, so the events are emitted once,
 * here, rather than eleven times at the surfaces. `surface` is required for
 * exactly that reason — the helper knows *what* is shared but only the caller
 * knows *where from*, and a default would collapse every surface into one
 * unreadable bucket.
 */
export async function shareEntity(
  entity: ShareEntity,
  surface: ShareSurface,
): Promise<ShareOutcome> {
  const text = buildShareText(entity);
  const target = { entityType: entity.type, entityId: entity.id, surface };

  const sheet = await presentSystemSheet(text, target);
  if (sheet !== 'unavailable') return sheet === 'failed' ? 'dismissed' : sheet;

  trackShareClicked(target);
  const done = (channel: ShareChannel, outcome: ShareOutcome): ShareOutcome => {
    trackShareCompleted({ ...target, channel, outcome });
    return outcome;
  };

  if (Platform.OS === 'web') {
    const nav: any = typeof navigator !== 'undefined' ? navigator : undefined;
    if (nav?.clipboard?.writeText) {
      try {
        await nav.clipboard.writeText(text.message);
        return done('clipboard', 'copied');
      } catch {
        return done('clipboard', 'dismissed');
      }
    }
  }
  // No channel at all — still reported, so «браузер не умеет делиться»
  // shows up as a dismissal instead of vanishing between the two events.
  return done('clipboard', 'dismissed');
}

// ── CORE-786 — sheet on a phone, popup everywhere else ──────────────────────

/** The two strings a one-field channel needs: what to say and what to call it. */
export interface ShareSheetText {
  subject: string;
  /** Must already contain the URL — the sheet gets no separate `url` field. */
  message: string;
}

/**
 * The props every `<ShareModal>` caller already holds. `shareOrOpenModal`
 * derives the sheet text from them with the modal's own fallbacks, so the OS
 * sheet and the popup send the same words.
 */
export interface ShareModalText {
  url: string;
  title: string;
  subject?: string;
  message?: string;
}

/**
 * Whether there is a system share sheet worth offering *instead of* the
 * in-app popup.
 *
 * Native: always. Web: only on a phone/tablet browser that implements the Web
 * Share API. Desktop browsers may implement it too (Chrome on Windows, Safari
 * on macOS), but their sheet is a poor cousin of the popup — no Telegram, no
 * WhatsApp — so on a desktop the popup stays the experience.
 */
export function hasSystemShareSheet(): boolean {
  if (Platform.OS !== 'web') return true;
  const nav: any = typeof navigator !== 'undefined' ? navigator : undefined;
  if (typeof nav?.share !== 'function') return false;
  const ua: string = nav.userAgent ?? '';
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  // iPadOS asks for the desktop site by default and reports a Mac UA; the
  // touch points are the only thing left that says «tablet».
  return /Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1;
}

type SheetResult =
  | ShareOutcome
  /** No sheet on this platform/browser — nothing was tracked. */
  | 'unavailable'
  /** A sheet exists but would not open (gesture lost, payload rejected) — reported as dismissed. */
  | 'failed';

/**
 * Hand the text to the OS sheet. Nothing is awaited before `navigator.share()`
 * — the Web Share API only resolves inside a user gesture, and an await here
 * would drop that gesture.
 */
async function presentSystemSheet(text: ShareSheetText, target: ShareTarget): Promise<SheetResult> {
  const { subject, message } = text;
  const done = (channel: ShareChannel, outcome: ShareOutcome): ShareOutcome => {
    trackShareCompleted({ ...target, channel, outcome });
    return outcome;
  };

  if (Platform.OS === 'web') {
    const nav: any = typeof navigator !== 'undefined' ? navigator : undefined;
    if (typeof nav?.share !== 'function') return 'unavailable';
    trackShareClicked(target);
    try {
      // No `url` field: the message already ends with the link, and the
      // sheet's own «Скопировать» concatenates whatever it is given.
      await nav.share({ title: subject, text: message });
      return done('web_share', 'shared');
    } catch (err: any) {
      done('web_share', 'dismissed');
      // AbortError is the user closing the sheet. Anything else means the
      // sheet never showed, and the caller may want to offer another way.
      return err?.name === 'AbortError' || /AbortError/.test(String(err?.message ?? '')) ? 'dismissed' : 'failed';
    }
  }

  trackShareClicked(target);
  try {
    await Share.share({ message });
    return done('native_sheet', 'shared');
  } catch {
    return done('native_sheet', 'dismissed');
  }
}

/**
 * The one share entry point for a button that also owns a `<ShareModal>`:
 * a phone gets the OS sheet, everything else gets the popup. Call it straight
 * from the press handler — the sheet must open inside the gesture.
 *
 * `openModal` is only invoked when no sheet was shown, so the popup's own
 * click/dismiss tracking never double-counts a sheet that did open. (A sheet
 * that failed to open has already been reported as dismissed; the popup then
 * counts as a second, separate attempt — a rare edge and an honest one.)
 */
export async function shareOrOpenModal(
  text: ShareModalText,
  target: ShareTarget,
  openModal: () => void,
): Promise<void> {
  if (hasSystemShareSheet()) {
    // Same fallbacks as ShareModal's `effectiveSubject` / `effectiveMessage`.
    const sheetText: ShareSheetText = {
      subject: text.subject ?? text.title,
      message: text.message ?? `${text.title} — ${text.url}`,
    };
    const result = await presentSystemSheet(sheetText, target);
    if (result !== 'failed' && result !== 'unavailable') return;
  }
  openModal();
}

/**
 * CORE-1795 — the OS sheet from inside the popup. When the popup carries actions of
 * its own (a repost into the feed) a phone opens it first, and the sheet becomes one of
 * its tiles; this hands the same text to the sheet. Call it inside the gesture.
 * Resolves true when a sheet was shown (shared or dismissed), false when there is none.
 */
export async function shareViaSystemSheet(text: ShareModalText, target: ShareTarget): Promise<boolean> {
  const sheetText: ShareSheetText = {
    subject: text.subject ?? text.title,
    message: text.message ?? `${text.title} — ${text.url}`,
  };
  const result = await presentSystemSheet(sheetText, target);
  return result !== 'failed' && result !== 'unavailable';
}

/** `shareOrOpenModal` for the four entity kinds with a localized template. */
export function shareEntityOrModal(
  entity: ShareEntity,
  surface: ShareSurface,
  openModal: () => void,
): Promise<void> {
  const { subject, message, url } = buildShareText(entity);
  return shareOrOpenModal(
    { url, title: subject, subject, message },
    { entityType: entity.type, entityId: entity.id, surface },
    openModal,
  );
}
